<template>
  <div class="policy-search-container">
    <!-- Back Button -->
    <div class="max-w-4xl mx-auto pt-4 px-4">
      <router-link to="/customerservice" class="inline-flex items-center text-blue-600 hover:text-blue-800">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back to Customer Service
      </router-link>
    </div>

    <!-- Header Section -->
    <div class="bg-blue-50 py-8 px-4 border-b border-blue-100">
      <div class="max-w-4xl mx-auto">
        <h1 class="text-2xl md:text-3xl font-bold text-center text-blue-800 mb-2">
          Policy Search
        </h1>
        <p class="text-center text-gray-600 max-w-2xl mx-auto">
          Find policies by policy number, customer name, or other details
        </p>
      </div>
    </div>
    
    <!-- Search Form Section -->
    <div class="max-w-4xl mx-auto py-6 px-4">
      <div class="bg-white shadow-md rounded-lg p-6">
        <form @submit.prevent="handleSearch" class="space-y-4">
          <!-- Search Type Tabs -->
          <div class="flex flex-wrap border-b border-gray-200 mb-4">
            <button
              v-for="(tab, index) in searchTabs"
              :key="index"
              type="button"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-4 font-medium text-sm focus:outline-none',
                activeTab === tab.id
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
              :aria-selected="activeTab === tab.id"
              role="tab"
            >
              {{ tab.label }}
            </button>
          </div>
          
          <!-- Policy Number Search -->
          <div v-if="activeTab === 'policy'" class="space-y-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="policy-number">
                Policy Number
              </label>
              <input
                v-model="searchForm.policyNumber"
                type="text"
                id="policy-number"
                class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                placeholder="Enter policy number"
              />
            </div>
          </div>
          
          <!-- Customer Name Search -->
          <div v-else-if="activeTab === 'customer'" class="space-y-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="customer-name">
                Customer Name
              </label>
              <input
                v-model="searchForm.customerName"
                type="text"
                id="customer-name"
                class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                placeholder="Enter first or last name"
              />
            </div>
          </div>
          
          <!-- Address Search -->
          <!-- 
          <div v-else-if="activeTab === 'address'" class="space-y-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="address">
                Property Address
              </label>
              <input
                v-model="searchForm.address"
                type="text"
                id="address"
                class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                placeholder="Enter street address"
              />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2" for="city">
                  City
                </label>
                <input
                  v-model="searchForm.city"
                  type="text"
                  id="city"
                  class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                />
              </div>
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2" for="state">
                  State
                </label>
                <select
                  v-model="searchForm.state"
                  id="state"
                  class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                >
                  <option value="">Select State</option>
                  <option v-for="state in statesList" :key="state.code" :value="state.code">
                    {{ state.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          -->
          
          <!-- Advanced Search -->
          <!--
          <div v-else-if="activeTab === 'advanced'" class="space-y-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="policy-type">
                Policy Type
              </label>
              <select
                v-model="searchForm.policyType"
                id="policy-type"
                class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
              >
                <option value="">All Policy Types</option>
                <option value="home">Home</option>
                <option value="auto">Auto</option>
                <option value="renters">Renters</option>
                <option value="condo">Condo</option>
                <option value="flood">Flood</option>
              </select>
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="status">
                Policy Status
              </label>
              <select
                v-model="searchForm.status"
                id="status"
                class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="date-range">
                Effective Date Range
              </label>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <input
                    v-model="searchForm.dateFrom"
                    type="date"
                    id="date-from"
                    class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
                <div>
                  <input
                    v-model="searchForm.dateTo"
                    type="date"
                    id="date-to"
                    class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </div>
            </div>
          </div>
          -->
          
          <!-- Search Button -->
          <div class="pt-4">
            <button
              type="submit"
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full md:w-auto"
              :disabled="isSearching"
            >
              <span v-if="isSearching">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </span>
              <span v-else>Search</span>
            </button>
          </div>
        </form>
      </div>
      
      <!-- Error Message -->
      <div v-if="error" class="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
        {{ error }}
      </div>
      
      <!-- Results Section -->
      <div v-if="searchResults.length > 0" class="mt-8">
        <h2 class="text-xl font-semibold mb-4">Search Results</h2>
        
        <div class="bg-white shadow-md rounded-lg overflow-hidden">
          <!-- Results Table for Desktop -->
          <div class="hidden md:block">
            <table class="min-w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Policy Number
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Effective
                  </th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(policy, index) in searchResults" :key="index">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                    {{ policy.policyNumber }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ policy.customerName }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ policy.type }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span 
                      :class="[
                        'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                        getStatusClass(policy.status)
                      ]"
                    >
                      {{ policy.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ formatDate(policy.effectiveDate) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <a 
                      href="#" 
                      @click.prevent="viewPolicy(policy)" 
                      class="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </a>
                    <a 
                      href="#"
                      @click.prevent="editPolicy(policy)"
                      class="text-indigo-600 hover:text-indigo-900"
                    >
                      Manage
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Results Cards for Mobile -->
          <div class="md:hidden">
            <div v-for="(policy, index) in searchResults" :key="index" class="border-b border-gray-200 p-4">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-blue-600 font-medium">{{ policy.policyNumber }}</p>
                  <p class="text-sm text-gray-900">{{ policy.customerName }}</p>
                  <div class="flex items-center mt-1">
                    <span 
                      :class="[
                        'px-2 inline-flex text-xs leading-5 font-semibold rounded-full mr-2',
                        getStatusClass(policy.status)
                      ]"
                    >
                      {{ policy.status }}
                    </span>
                    <span class="text-xs text-gray-500">{{ policy.type }}</span>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">
                    Effective: {{ formatDate(policy.effectiveDate) }}
                  </p>
                </div>
                <div class="flex flex-col space-y-2">
                  <button 
                    @click="viewPolicy(policy)" 
                    class="text-blue-600 text-sm"
                  >
                    View
                  </button>
                  <button 
                    @click="editPolicy(policy)" 
                    class="text-indigo-600 text-sm"
                  >
                    Manage
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Pagination -->
          <div v-if="searchResults.length > 0" class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div class="flex justify-between items-center">
              <div class="text-sm text-gray-700">
                Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalResults }} results
              </div>
              <div class="flex space-x-2">
                <button 
                  @click="prevPage" 
                  :disabled="currentPage === 1"
                  :class="[
                    'px-3 py-1 border rounded-md text-sm',
                    currentPage === 1 
                      ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  ]"
                >
                  Previous
                </button>
                <button 
                  @click="nextPage"
                  :disabled="currentPage >= totalPages"
                  :class="[
                    'px-3 py-1 border rounded-md text-sm',
                    currentPage >= totalPages 
                      ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  ]"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- No Results Message -->
      <div v-else-if="hasSearched && !isSearching" class="mt-8 p-6 bg-gray-50 rounded-lg text-center">
        <svg class="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="text-gray-600">No policies found matching your search criteria.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePolicySearchService } from '@/services/PolicySearchService';

const router = useRouter();
const policySearchService = usePolicySearchService();

// Search tabs
const searchTabs = [
  { id: 'policy', label: 'Policy Number' },
  { id: 'customer', label: 'Customer Name' },
  // Commented out address and advanced search tabs
  // { id: 'address', label: 'Address' },
  // { id: 'advanced', label: 'Advanced' }
];

// Form state
const activeTab = ref('policy');
const searchForm = reactive({
  policyNumber: '',
  customerName: '',
  address: '',
  city: '',
  state: '',
  policyType: '',
  status: '',
  dateFrom: '',
  dateTo: ''
});

// Results state
const isSearching = computed(() => policySearchService.isLoading.value);
const error = computed(() => policySearchService.error.value);
const hasSearched = ref(false);
const searchResults = ref([]);
const currentPage = ref(1);
const resultsPerPage = 10;
const totalResults = ref(0);

// Mock state list (would come from API in real implementation)
const statesList = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  // Additional states would be included in a real implementation
];

// Computed properties for pagination
const paginationStart = computed(() => {
  return (currentPage.value - 1) * resultsPerPage + 1;
});

const paginationEnd = computed(() => {
  return Math.min(currentPage.value * resultsPerPage, totalResults.value);
});

const totalPages = computed(() => {
  return Math.ceil(totalResults.value / resultsPerPage);
});

// Methods
const handleSearch = async () => {
  // Reset pagination
  currentPage.value = 1;
  
  // Validate form based on active tab
  if (!validateSearch()) {
    return;
  }
  
  try {
    let results = [];
    
    // Use appropriate search method based on active tab
    if (activeTab.value === 'policy') {
      results = await policySearchService.searchByPolicyNumber(searchForm.policyNumber);
    } else if (activeTab.value === 'customer') {
      results = await policySearchService.searchByCustomerName(searchForm.customerName);
    } 
    // Commented out address and advanced search methods
    /*
    else if (activeTab.value === 'address') {
      results = await policySearchService.searchByAddress({
        address: searchForm.address,
        city: searchForm.city,
        state: searchForm.state
      });
    } else if (activeTab.value === 'advanced') {
      results = await policySearchService.searchByAdvanced({
        policyType: searchForm.policyType,
        status: searchForm.status,
        dateFrom: searchForm.dateFrom,
        dateTo: searchForm.dateTo
      });
    }
    */
    
    // Check if we have real API results, otherwise fall back to mock data
    if (results.length === 0 && !error.value) {
      // In a real implementation, this would be an API call
      // For now, we'll use mock data if the real API returned no results
      searchResults.value = getMockSearchResults();
    } else {
      // Format the real API results
      searchResults.value = results.map(policy => ({
        id: policy.policy_id,
        policyNumber: policy.policy_number,
        customerName: policy.writing_company_name,
        type: policy.policy_type,
        status: policy.status || 'Active',
        effectiveDate: policy.start_date,
        address: policy.address || '',
        city: policy.city || '',
        state: policy.state || ''
      }));
    }
    
    totalResults.value = searchResults.value.length;
    hasSearched.value = true;
  } catch (err) {
    console.error('Error searching policies:', err);
    searchResults.value = [];
  }
};

const validateSearch = () => {
  if (activeTab.value === 'policy' && !searchForm.policyNumber.trim()) {
    return false;
  }
  
  if (activeTab.value === 'customer' && !searchForm.customerName.trim()) {
    return false;
  }
  
  /*
  if (activeTab.value === 'address' && !searchForm.address.trim()) {
    return false;
  }
  */
  
  return true;
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const viewPolicy = (policy) => {
  console.log('View policy:', policy);
  // In a real implementation, this would navigate to the policy detail page
  // router.push({ name: 'policyDetail', params: { id: policy.id } });
};

const editPolicy = (policy) => {
  console.log('Edit policy:', policy);
  // In a real implementation, this would navigate to the policy management page
  // router.push({ name: 'policyManagement', params: { id: policy.id } });
};

const formatDate = (dateString) => {
  return policySearchService.formatDateString(dateString);
};

const getStatusClass = (status) => {
  const statusClasses = {
    'active': 'bg-green-100 text-green-800',
    'pending': 'bg-yellow-100 text-yellow-800',
    'expired': 'bg-red-100 text-red-800',
    'cancelled': 'bg-gray-100 text-gray-800'
  };
  
  return statusClasses[status.toLowerCase()] || 'bg-gray-100 text-gray-800';
};

// Mock data generation (would be replaced by API calls in real implementation)
const getMockSearchResults = () => {
  const results = [];
  const statuses = ['Active', 'Pending', 'Expired', 'Cancelled'];
  const types = ['Home', 'Auto', 'Renters', 'Condo', 'Flood'];
  
  // Generate between 0 and 15 results based on search criteria
  const count = Math.floor(Math.random() * 16);
  
  for (let i = 0; i < count; i++) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - Math.floor(Math.random() * 365 * 2));
    
    results.push({
      id: `POL-${Math.floor(Math.random() * 10000)}`,
      policyNumber: `POL-${Math.floor(Math.random() * 10000)}`,
      customerName: `Customer ${i + 1}`,
      type: types[Math.floor(Math.random() * types.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      effectiveDate: startDate.toISOString().split('T')[0],
      address: `${Math.floor(Math.random() * 9999) + 1} Main Street`,
      city: 'Anytown',
      state: 'FL'
    });
  }
  
  return results;
};
</script>

<style scoped>
/* Component-specific styles here */

/* Fix for submit button icon alignment */
.mobile-enhanced-button span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-enhanced-button svg.iconify {
  margin-left: 0.5rem;
  width: 20px;
  height: 20px;
  vertical-align: middle;
}

.flex-responsive-row {
  display: flex;
  flex-direction: column;
}

@media (min-width: 640px) {
  .flex-responsive-row {
    flex-direction: row;
  }
}
</style> 